/**
 * B6/B7/B9 + C2 · Plan templates: build reusable weekly plans, duplicate,
 * publish to the community gallery (catalog-only), and assign to several
 * students at once — each gets the items transposed to their own range.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { api } from '../../lib/api.ts';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Modal,
  Spinner,
  Tag,
  Textarea,
} from '../../lib/ui.tsx';

interface TemplateItem {
  id: string;
  exerciseId: string;
  orderIndex: number;
  targetDays: string;
  frequencyPerWeek: number;
}

interface Template {
  id: string;
  name: string;
  description: string;
  publishedToGallery: boolean;
  studioId: string | null;
  items: TemplateItem[];
}

interface Exercise {
  id: string;
  name: string;
  level: string;
  isCatalog: boolean;
  estDurationSec: number;
}

interface StudentRow {
  userId: string;
  status: string;
  user: { id: string; name: string; email: string };
}

interface DraftItem {
  exerciseId: string;
  frequencyPerWeek: number;
}

export default function TemplatesPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<DraftItem[]>([]);
  const [search, setSearch] = useState('');
  const [assignTemplate, setAssignTemplate] = useState<Template | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Template[]>('/api/templates'),
  });
  const exercises = useQuery({
    queryKey: ['exercises'],
    queryFn: () => api<Exercise[]>('/api/exercises'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['templates'] });

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description, items };
      return editingId
        ? api(`/api/templates/${editingId}`, { method: 'PATCH', body })
        : api('/api/templates', { body });
    },
    onSuccess: () => {
      setModalOpen(false);
      invalidate();
    },
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api(`/api/templates/${id}/duplicate`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/templates/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const publish = useMutation({
    mutationFn: (v: { id: string; published: boolean }) =>
      api(`/api/templates/${v.id}/publish`, { body: { published: v.published } }),
    onSuccess: () => {
      setPublishingId(null);
      invalidate();
    },
  });

  function openCreate() {
    setEditingId(null);
    setName('');
    setDescription('');
    setItems([]);
    setSearch('');
    save.reset();
    setModalOpen(true);
  }

  function openEdit(tpl: Template) {
    setEditingId(tpl.id);
    setName(tpl.name);
    setDescription(tpl.description);
    setItems(tpl.items.map((it) => ({ exerciseId: it.exerciseId, frequencyPerWeek: it.frequencyPerWeek })));
    setSearch('');
    save.reset();
    setModalOpen(true);
  }

  const exList = exercises.data ?? [];
  const exName = (exerciseId: string) => {
    const ex = exList.find((e) => e.id === exerciseId);
    return ex ? exLabel(ex) : 'Unknown exercise';
  };
  const filtered = search.trim()
    ? exList.filter((e) => e.name.toLowerCase().includes(search.trim().toLowerCase()))
    : exList;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Plan templates</h1>
        <Button onClick={openCreate}>+ New template</Button>
      </div>

      {toast && (
        <Card className="border-emerald-200 bg-emerald-50">
          <p className="text-sm text-emerald-800">{toast} ✓</p>
        </Card>
      )}

      <ErrorText error={duplicate.error ?? remove.error} />

      {templates.isLoading ? (
        <Spinner />
      ) : templates.error ? (
        <ErrorText error={templates.error} />
      ) : !templates.data || templates.data.length === 0 ? (
        <EmptyState
          title="No templates yet"
          hint="Bundle your go-to weekly routine once and assign it to any student in two clicks."
          action={<Button onClick={openCreate}>Create your first template</Button>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.data.map((tpl) => (
            <Card key={tpl.id} className="flex flex-col">
              <div className="mb-1 flex items-start justify-between gap-2">
                <p className="min-w-0 text-sm font-semibold text-slate-900">{tpl.name}</p>
                {tpl.publishedToGallery && <Tag tone="green">In gallery</Tag>}
              </div>
              {tpl.description && <p className="mb-2 line-clamp-2 text-xs text-slate-500">{tpl.description}</p>}
              <p className="mb-3 text-xs text-slate-400">
                {tpl.items.length} exercise{tpl.items.length === 1 ? '' : 's'}
              </p>
              <div className="mt-auto flex flex-wrap gap-1.5">
                <Button className="px-2.5 py-1 text-xs" onClick={() => setAssignTemplate(tpl)}>
                  Assign to students
                </Button>
                <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => openEdit(tpl)}>
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  className="px-2.5 py-1 text-xs"
                  onClick={() => duplicate.mutate(tpl.id)}
                  disabled={duplicate.isPending}
                >
                  Duplicate
                </Button>
                <Button
                  variant="ghost"
                  className="px-2.5 py-1 text-xs"
                  onClick={() => {
                    setPublishingId(tpl.id);
                    publish.mutate({ id: tpl.id, published: !tpl.publishedToGallery });
                  }}
                  disabled={publish.isPending && publishingId === tpl.id}
                >
                  {tpl.publishedToGallery ? 'Unpublish' : 'Publish to gallery'}
                </Button>
                <Button
                  variant="ghost"
                  className="px-2.5 py-1 text-xs text-red-600"
                  onClick={() => {
                    if (window.confirm(`Delete template "${tpl.name}"?`)) remove.mutate(tpl.id);
                  }}
                  disabled={remove.isPending}
                >
                  Delete
                </Button>
              </div>
              {publishingId === tpl.id && <ErrorText error={publish.error} />}
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Edit template' : 'New template'}>
        <div className="space-y-3">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Belting fundamentals" />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>

          {items.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium tracking-wide text-slate-400 uppercase">Exercises in this template</p>
              {items.map((item, i) => (
                <div key={`${item.exerciseId}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5">
                  <p className="min-w-0 truncate text-sm text-slate-700">{exName(item.exerciseId)}</p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <select
                      className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs"
                      value={item.frequencyPerWeek}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((it, idx) =>
                            idx === i ? { ...it, frequencyPerWeek: Number(e.target.value) } : it,
                          ),
                        )
                      }
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <option key={n} value={n}>
                          {n}× / week
                        </option>
                      ))}
                    </select>
                    <button
                      className="rounded px-1.5 text-red-500 hover:bg-red-50"
                      onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Field label="Add exercises" hint="Only catalog exercises can go into gallery-published templates.">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the library…" />
          </Field>
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {exercises.isLoading ? (
              <Spinner />
            ) : (
              filtered.slice(0, 30).map((ex) => (
                <div key={ex.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-1.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-700">{exLabel(ex)}</p>
                    <p className="text-[11px] text-slate-400">
                      {ex.level}
                      {ex.isCatalog ? ' · catalog' : ''}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    className="shrink-0 px-2 py-1"
                    onClick={() => setItems((prev) => [...prev, { exerciseId: ex.id, frequencyPerWeek: 3 }])}
                  >
                    + Add
                  </Button>
                </div>
              ))
            )}
          </div>

          <ErrorText error={save.error} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={!name.trim() || items.length === 0 || save.isPending}>
              {editingId ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </div>
      </Modal>

      <AssignModal
        template={assignTemplate}
        onClose={() => setAssignTemplate(null)}
        onAssigned={(n) => {
          setAssignTemplate(null);
          setToast(`Assigned to ${n} student${n === 1 ? '' : 's'}, transposed per student`);
          setTimeout(() => setToast(null), 5000);
        }}
      />
    </div>
  );
}

// ── C2 · Multi-student assignment ───────────────────────────────────────────

function AssignModal({
  template,
  onClose,
  onAssigned,
}: {
  template: Template | null;
  onClose: () => void;
  onAssigned: (n: number) => void;
}) {
  const qc = useQueryClient();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const students = useQuery({
    queryKey: ['students'],
    queryFn: () => api<StudentRow[]>('/api/students'),
    enabled: template !== null,
  });

  const assign = useMutation({
    mutationFn: () =>
      api('/api/plans/from-template', {
        body: { templateId: template?.id, studentIds: [...checked] },
      }),
    onSuccess: () => {
      const n = checked.size;
      setChecked(new Set());
      void qc.invalidateQueries({ queryKey: ['plans'] });
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
      onAssigned(n);
    },
  });

  const active = (students.data ?? []).filter((s) => s.status === 'active');

  return (
    <Modal open={template !== null} onClose={onClose} title={`Assign "${template?.name ?? ''}"`}>
      <p className="mb-3 text-xs text-slate-500">
        Each student gets this week's plan with every exercise transposed to their own range (B2).
      </p>
      {students.isLoading ? (
        <Spinner />
      ) : active.length === 0 ? (
        <p className="text-sm text-slate-500">No active students yet.</p>
      ) : (
        <div className="max-h-64 space-y-1.5 overflow-y-auto">
          {active.map((s) => (
            <label key={s.userId} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <input
                type="checkbox"
                checked={checked.has(s.userId)}
                onChange={() =>
                  setChecked((prev) => {
                    const next = new Set(prev);
                    if (next.has(s.userId)) next.delete(s.userId);
                    else next.add(s.userId);
                    return next;
                  })
                }
              />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{s.user.name}</span>
              <span className="shrink-0 text-xs text-slate-400">{s.user.email}</span>
            </label>
          ))}
        </div>
      )}
      <ErrorText error={assign.error} />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => assign.mutate()} disabled={checked.size === 0 || assign.isPending}>
          Assign ({checked.size})
        </Button>
      </div>
    </Modal>
  );
}
